// ─────────────────────────────────────────────────────────────────────────────
// BlogPage.jsx — uses shared components
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react'
import { useSearchParams }             from 'react-router-dom'
import { blogPosts }                   from '@data/blogPosts'
import PageHero                        from '@components/common/PageHero'
import SectionHeading                  from '@components/common/SectionHeading'
import BlogCard                        from '@components/common/BlogCard'
import SEOHead                         from '@components/common/SEOHead'

const POSTS_PER_PAGE = 12

// Sort newest first by date
const sortedPosts = [...blogPosts].sort(
  (a, b) => new Date(b.date) - new Date(a.date)
)

// ── Fade-in hook ──────────────────────────────────────────────────────────────
function useFadeIn() {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true) },
      { threshold: 0.05 }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])
  return [ref, vis]
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null

  // Smart page range: max 7 numbers with ellipsis
  const getRange = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages]
    if (currentPage >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages]
  }

  const NavBtn = ({ label, page, disabled = false, isActive = false }) => (
    <button
      onClick={() => !disabled && onPageChange(page)}
      disabled={disabled}
      style={{
        display:     'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth:    '36px', height: '36px', padding: '0 6px',
        fontFamily:  "'DM Sans', sans-serif",
        fontSize:    '14px', fontWeight: isActive ? 700 : 400,
        color:       isActive ? '#ffffff' : disabled ? '#c8c7c0' : '#44443e',
        background:  isActive ? '#007935' : 'transparent',
        border:      `1px solid ${isActive ? '#007935' : '#e0dfda'}`,
        cursor:      disabled ? 'not-allowed' : 'pointer',
        marginRight: '3px',
        transition:  'all 0.15s ease',
      }}
      onMouseEnter={e => {
        if (!disabled && !isActive) {
          e.currentTarget.style.background  = '#f0f0ee'
          e.currentTarget.style.borderColor = '#007935'
          e.currentTarget.style.color       = '#007935'
        }
      }}
      onMouseLeave={e => {
        if (!disabled && !isActive) {
          e.currentTarget.style.background  = 'transparent'
          e.currentTarget.style.borderColor = '#e0dfda'
          e.currentTarget.style.color       = '#44443e'
        }
      }}
    >
      {label}
    </button>
  )

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      justifyContent: 'center',
      marginTop: '56px', flexWrap: 'wrap',
    }}>
      <span style={{
        fontFamily: "'DM Sans',sans-serif",
        fontSize: '13px', color: '#88877f',
        marginRight: '16px',
      }}>
        Page {currentPage} of {totalPages}
      </span>

      <NavBtn label="«" page={1}               disabled={currentPage === 1} />
      <NavBtn label="‹" page={currentPage - 1} disabled={currentPage === 1} />

      {getRange().map((p, i) =>
        p === '...'
          ? <span key={`e${i}`} style={{ padding: '0 6px', color: '#88877f', lineHeight: '36px', marginRight: '3px' }}>…</span>
          : <NavBtn key={p} label={p} page={p} isActive={p === currentPage} />
      )}

      <NavBtn label="›" page={currentPage + 1} disabled={currentPage === totalPages} />
      <NavBtn label="»" page={totalPages}       disabled={currentPage === totalPages} />
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const totalPages = Math.ceil(sortedPosts.length / POSTS_PER_PAGE)

  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1', 10)
    return Math.max(1, Math.min(p, totalPages))
  })

  const [ref, vis] = useFadeIn()
  const topRef     = useRef(null)

  const startIndex = (page - 1) * POSTS_PER_PAGE
  const pagePosts  = sortedPosts.slice(startIndex, startIndex + POSTS_PER_PAGE)

  // Sync URL → page state
  useEffect(() => {
    const p = parseInt(searchParams.get('page') || '1', 10)
    setPage(Math.max(1, Math.min(p, totalPages)))
  }, [searchParams, totalPages])

  const handlePageChange = (newPage) => {
    setPage(newPage)
    setSearchParams({ page: newPage })
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <main>
      <SEOHead
        title="News & Insights"
        description="Expert insights on animal nutrition, poultry health, and livestock management from the technical team at Salient Agrinutrition, Sri Lanka."
        path="/blog"
      />

      {/* ── PageHero — shared component ── */}
      <PageHero
        title="News & Blog"
        breadcrumbs={[
          { label: 'Home',        to: '/' },
          { label: 'News & Blog' },
        ]}
      />

      {/* ── Blog grid ── */}
      <section ref={topRef} style={{ background: '#ffffff', padding: '64px 0 72px' }}>
        <div style={{
          maxWidth: 'var(--container-max)', margin: '0 auto',
          padding:  '0 var(--container-padding)',
        }}>

          {/* SectionHeading — shared component with right-side post count */}
          <div style={{ marginBottom: '36px' }}>
            <SectionHeading
              title="Salient Blogs"
              subtitle={`Showing ${startIndex + 1}–${Math.min(startIndex + POSTS_PER_PAGE, sortedPosts.length)} of ${sortedPosts.length} articles`}
            />
          </div>

          {/* BlogCard grid — shared BlogCard component */}
          <div
            ref={ref}
            className="blog-grid"
            style={{
              display:             'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap:                 '36px 28px',
              alignItems:          'start',
            }}
          >
            {pagePosts.map((post, i) => (
              <BlogCard
                key={post.id}
                post={post}
                index={i}
                triggered={vis}
              />
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />

        </div>
      </section>

      <style>{`
        @media (max-width: 900px) { .blog-grid { grid-template-columns: 1fr 1fr !important; gap: 24px 16px !important; } }
        @media (max-width: 560px) { .blog-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </main>
  )
}